import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';

const PublicSubmission = () => {
  const [accessId, setAccessId] = useState('');
  const [gradeData, setGradeData] = useState({});
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const classes = [
    'Advanced Biology F25',
    'Adv Algebra 2 - Rose',
    'Schwarz: English II',
    'Spanish 1 - Fall 2025',
    'Adv Design Applications'
  ];

  // Initialize grade data for all classes
  useEffect(() => {
    const initialData = {};
    classes.forEach(className => {
      initialData[className] = {
        formativePercent: '',
        summativePercent: '',
        notes: ''
      };
    });
    setGradeData(initialData);
  }, []);

  const handleInputChange = (className, field, value) => {
    setGradeData(prev => ({
      ...prev,
      [className]: {
        ...prev[className],
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    // Validate Access ID
    if (accessId !== 'MANUS') {
      setSubmitMessage('❌ Invalid Access ID. Submission denied.');
      setIsSubmitting(false);
      return;
    }

    try {
      const promises = [];
      let submissionCount = 0;

      for (const [className, data] of Object.entries(gradeData)) {
        const formativePercent = parseFloat(data.formativePercent);
        const summativePercent = parseFloat(data.summativePercent);

        // Skip if formative grade is invalid (summative can be blank for early year)
        // This allows early-year submissions with only formative grades
        if (isNaN(formativePercent)) {
          continue;
        }

        // Calculate overall grade (35% formative + 65% summative)
        const overallPercent = (formativePercent * 0.35) + (summativePercent * 0.65);

        // Calculate overall grade - if no summative grade, use formative grade only
        let finalOverallPercent;
        if (isNaN(summativePercent) || summativePercent === 0) {
          // No summative grade yet - use formative grade as overall
          finalOverallPercent = formativePercent;
        } else {
          // Both grades available - use weighted calculation
          finalOverallPercent = (formativePercent * 0.35) + (summativePercent * 0.65);
        }

        promises.push(
          addDoc(collection(db, 'grades'), {
            date,
            className,
            formativePercent,
            summativePercent: isNaN(summativePercent) ? null : summativePercent,
            overallPercent: finalOverallPercent,
            notes: data.notes || '',
            submittedBy: 'AI_AGENT',
            timestamp: new Date().toISOString()
          })
        );
        submissionCount++;
      }

      if (promises.length > 0) {
        await Promise.all(promises);
        setSubmitMessage(`✅ Successfully submitted grades for ${submissionCount} class(es)!`);

        // Reset form
        const resetData = {};
        classes.forEach(className => {
          resetData[className] = {
            formativePercent: '',
            summativePercent: '',
            notes: ''
          };
        });
        setGradeData(resetData);
        setAccessId('');
      } else {
        setSubmitMessage('❌ No valid grade data to submit.');
      }
    } catch (error) {
      console.error('Error submitting grades:', error);
      setSubmitMessage('❌ Error submitting grades. Please try again.');
    }

    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white">
      {/* Header */}
      <header className="bg-[#1a1a1a] border-b border-[#4d4d4d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">🤖 AI Agent Grade Portal</h1>
              <p className="text-gray-400 mt-1">Automated grade submission endpoint for AI agents</p>
            </div>
            <a
              href="/"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded font-medium transition-colors"
            >
              ← Back to Site
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-8">
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-2">🤖 AI Agent Grade Submission</h2>
            <p className="text-gray-400">
              Designed for automated AI agents to submit grade data. Fill in the clearly labeled fields below with your grade percentages.
            </p>
            <div className="mt-4 p-3 bg-blue-600/10 border border-blue-500/30 rounded">
              <p className="text-sm text-blue-400">
                <strong>AI Instructions:</strong> Enter "MANUS" as the Access ID, select the date, then fill in grade percentages for each class. If there are no summative grades yet (beginning of year), leave the summative field blank - the overall grade will automatically use just the formative grade.
              </p>
            </div>
          </div>

          {submitMessage && (
            <div className={`mb-6 p-4 rounded ${
              submitMessage.includes('✅')
                ? 'bg-green-600/20 border border-green-500/30'
                : 'bg-red-600/20 border border-red-500/30'
            }`}>
              <p className={submitMessage.includes('✅') ? 'text-green-400' : 'text-red-400'}>
                {submitMessage}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Access ID Field */}
            <div className="max-w-md">
              <label htmlFor="accessId" className="block text-sm font-medium mb-2">
                Access ID <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                id="accessId"
                value={accessId}
                onChange={(e) => setAccessId(e.target.value)}
                className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                placeholder="Enter access ID"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Required for AI agent authentication
              </p>
            </div>

            {/* Date Selector */}
            <div className="max-w-md">
              <label htmlFor="date" className="block text-sm font-medium mb-2">
                Submission Date
              </label>
              <input
                type="date"
                id="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* AI-Friendly Grade Entry Fields */}
            <div>
              <h3 className="text-lg font-medium mb-4">Grade Data Entry</h3>
              <div className="text-sm text-gray-400 mb-4">
                Fill in the grade percentages for each class. Leave blank if no grade for that day.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {classes.map((className) => (
                  <div key={className} className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-4">
                    <h4 className="font-medium text-white mb-3">{className}</h4>

                    <div className="space-y-3">
                      {/* Formative Grade */}
                      <div>
                        <label className="block text-xs text-blue-400 mb-1">
                          {className} Formative Grade (35%):
                        </label>
                        <input
                          type="number"
                          value={gradeData[className]?.formativePercent || ''}
                          onChange={(e) => handleInputChange(className, 'formativePercent', e.target.value)}
                          className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white text-center focus:outline-none focus:border-blue-500"
                          placeholder={`Put the ${className} formative grade here`}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>

                      {/* Summative Grade */}
                      <div>
                        <label className="block text-xs text-red-400 mb-1">
                          {className} Summative Grade (65%):
                        </label>
                        <input
                          type="number"
                          value={gradeData[className]?.summativePercent || ''}
                          onChange={(e) => handleInputChange(className, 'summativePercent', e.target.value)}
                          className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white text-center focus:outline-none focus:border-red-500"
                          placeholder={`Put the ${className} summative grade here (leave blank if none yet)`}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>

                      {/* Overall Preview */}
                      {(() => {
                        const formativePercent = parseFloat(gradeData[className]?.formativePercent || 0);
                        const summativePercent = parseFloat(gradeData[className]?.summativePercent || 0);

                        let overallPercent = '';
                        if (!isNaN(formativePercent) && formativePercent > 0) {
                          if (isNaN(summativePercent) || summativePercent === 0) {
                            // No summative grade - use formative as overall
                            overallPercent = formativePercent.toFixed(1);
                          } else {
                            // Both grades - use weighted calculation
                            overallPercent = ((formativePercent * 0.35) + (summativePercent * 0.65)).toFixed(1);
                          }
                        }

                        return overallPercent ? (
                          <div className="text-xs text-green-400">
                            {className} Overall: {overallPercent}%
                          </div>
                        ) : null;
                      })()}

                      {/* Notes */}
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">
                          {className} Notes (Optional):
                        </label>
                        <input
                          type="text"
                          value={gradeData[className]?.notes || ''}
                          onChange={(e) => handleInputChange(className, 'notes', e.target.value)}
                          className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white text-sm focus:outline-none focus:border-gray-500"
                          placeholder={`Optional notes for ${className}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-3 rounded font-medium transition-colors ${
                  isSubmitting
                    ? 'bg-gray-600 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Grades'}
              </button>
            </div>
          </form>

          {/* Footer Note */}
          <div className="mt-8 pt-6 border-t border-[#4d4d4d]">
            <p className="text-sm text-gray-500 text-center">
              🤖 AI Agent Endpoint - Only authorized AI agents with proper access credentials. All submissions are logged and monitored.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PublicSubmission;
